import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /** @type {(event: VariantSelectedEvent) => void} */
  #boundUpdateQuickAddButtonState = this.#updateQuickAddButtonState.bind(this);

  /** @type {boolean} */
  #hasWiredModalVariantSync = false;

  #productLoadId = 0; // Track product load requests to prevent outdated responses from updating the modal.

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    // ✅ FIX #1: Always override any existing ?variant= on the product card link
    // with the actual selected variant from the card (otherwise you often fetch "first" variant).
    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#boundUpdateQuickAddButtonState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#boundUpdateQuickAddButtonState);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;

    // ✅ FIX #2: Some implementations of updateVariantPicker() mutate the passed HTML.
    // Always pass a clone so we don't accidentally "eat" the picker out of cached content.
    variantPicker.updateVariantPicker(/** @type {Element} */ (newHtml.cloneNode(true)));
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */

  // Allow Quick Add to load a different product when a color swatch is clicked.
  // handleClick = async (event) => {   
  handleClick = async (event, productUrl = this.productPageUrl) => { 
    event.preventDefault();

    // const currentUrl = this.productPageUrl;
    const currentUrl = productUrl;

    const loadId = ++this.#productLoadId; // Identify the latest product load request.

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);

      if (loadId !== this.#productLoadId) return; // Ignore stale responses when users switch colors before previous requests finish.

      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        const shortDesc = html.querySelector('.short-description');

        // IMPORTANT: clone before appending to avoid moving nodes out of the parsed doc in unexpected ways
        if (shortDesc && gridElement) {
          gridElement.appendChild(shortDesc.cloneNode(true));
        }

        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));

      // Render modal
      // await this.updateQuickAddModal(freshContent);
      await this.updateQuickAddModal(freshContent, currentUrl);  // Pass the selected product URL to keep modal content and links in sync.

      // Keep Horizon behavior: update the in-modal picker with server-rendered html
      // (but pass clone so cache doesn't get mutated).
      this.#updateVariantPicker(productGrid);
    }


    
    // this.#openQuickAddModal();

    // Keep the existing modal open when switching between color products.
    const dialog = document.getElementById('quick-add-dialog');

    if (dialog) {
      dialog.activeQuickAddComponent = this;
    }

    if (dialog?.refs?.dialog?.open) {
      this.#resetScroll();
    } else {
      this.#openQuickAddModal();
    }

  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      return html;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Ensure changes in modal pills update the actual add-to-cart variant id
   * (fallback if theme wiring breaks due to DOM restructuring).
   * @param {Element} modalContent
   */
  #wireModalVariantPillsToForm(modalContent) {
    if (this.#hasWiredModalVariantSync) return;
    this.#hasWiredModalVariantSync = true;

    modalContent.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type !== 'radio') return;

      // Horizon typically uses data-variant-id on pill radios
      const variantId = target.dataset.variantId;
      if (!variantId) return;

      const idInput =
        modalContent.querySelector('product-form-component input[name="id"]') ||
        modalContent.querySelector('form[action*="/cart/add"] input[name="id"]') ||
        modalContent.querySelector('form[action*="/cart/add.js"] input[name="id"]');

      if (idInput instanceof HTMLInputElement && idInput.value !== variantId) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid, productUrl = this.productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    // Pull elements we want to position
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const custominventory = productGrid.querySelector('.custom-inventory');
    const colorPicker = productGrid.querySelector('.color-picker');
    const shortDescription = productGrid.querySelector('.short-description');

    const productFit = productGrid.querySelector('.product-details .details-header > .group-block-content > rte-formatter p');

    // ✅ If variantPicker is missing at this stage, do NOT proceed with a layout that drops it.
    // Better to render the grid as-is than to accidentally hide selection UI.
    // (This also makes debugging obvious.)
    if (!variantPicker) {
      morph(modalContent, productGrid);
      this.#syncVariantSelection(modalContent);
      this.#wireModalVariantPillsToForm(modalContent);
      return;
    }

    /* TITLE */
    const productTitle = document.createElement('a');
    // productTitle.textContent = this.dataset.productTitle || '';
    // productTitle.href = this.productPageUrl;

    
    // Update the mobile product title and link to match the selected color product.

    const titleLink = productGrid.querySelector('.view-product-title a');

    productTitle.textContent =
      titleLink?.textContent?.trim() || this.dataset.productTitle || '';
    productTitle.href = titleLink?.href || productUrl;



    /* MAIN WRAPPER */
    const productWrapper = document.createElement('div');
    productWrapper.classList.add('product-content-wrapper');

    /* HEADER */
    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');

    if(productFit)
      productHeader.appendChild(productFit);

    productHeader.appendChild(productTitle);

    if (productPrice) {
      productHeader.appendChild(productPrice);
    }

    productWrapper.appendChild(productHeader);

    if (colorPicker) {
      productWrapper.appendChild(colorPicker);
    }

    if (shortDescription) {
      productWrapper.appendChild(shortDescription);
    }

    /**
     * ✅ CRITICAL ORDER:
     * Move variantPicker + productFormComponent into the new wrapper BEFORE removing productDetails.
     * Otherwise removing productDetails will remove the picker entirely (if it lived inside productDetails).
     */
    productWrapper.appendChild(variantPicker);

    /* BUTTON WRAPPER */
    if (productFormComponent) {
      const buttonWrapper = document.createElement('div');
      buttonWrapper.classList.add('product-buttons');

      // Keep the real product-form-component intact (do not clone)
      buttonWrapper.appendChild(productFormComponent);

      // const productBtn = document.createElement('a');
      // productBtn.classList.add('go-product-btn');
      // productBtn.href = this.productPageUrl;
      // productBtn.textContent = 'GO TO PRODUCT';

      // buttonWrapper.appendChild(productBtn);
      productWrapper.appendChild(buttonWrapper);
    }

    
    const shippingWrapper = document.createElement('div');
    shippingWrapper.classList.add('shipping-div');

    if (custominventory) {
      shippingWrapper.appendChild(custominventory);
    }

    const productBtn = document.createElement('a');
    productBtn.classList.add('go-product-btn');
    productBtn.href = this.productPageUrl;
    productBtn.textContent = 'GO TO PRODUCT';

    shippingWrapper.appendChild(productBtn);

    productWrapper.appendChild(shippingWrapper);


    // Remove old details AFTER we've moved needed nodes out
    productDetails?.remove();

    // Add new wrapper
    productGrid.appendChild(productWrapper);

    /**
     * Optional: preserve Horizon’s mobile behavior if your CSS expects it.
     * If your theme hides .product-details in quick add on mobile, you typically want picker/form at root.
     * Since we already removed .product-details and created a wrapper, we don't need to re-parent further.
     * (Leaving this block as a no-op.)
     */
    if (isMobileBreakpoint()) {
      // no-op
    }

    morph(modalContent, productGrid);

    // ✅ Fix "adds first variant": ensure modal starts on the card’s selected variant
    // and ensure the form posts the correct id even if wiring is brittle.
    this.#syncVariantSelection(modalContent);
    this.#wireModalVariantPillsToForm(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Try to check the corresponding pill radio in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }

    // Fallback: force the add-to-cart hidden variant id
    const idInput =
      modalContent.querySelector('product-form-component input[name="id"]') ||
      modalContent.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      modalContent.querySelector('form[action*="/cart/add.js"] input[name="id"]');

    if (idInput instanceof HTMLInputElement && idInput.value !== selectedVariantId) {
      idInput.value = selectedVariantId;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    this.addEventListener('click', this.handleColorClick); // Handle color swatch clicks only within the Quick Add modal.
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }


  // Intercept color swatch navigation and load the selected product in the same modal.

  handleColorClick = (event) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) return;

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.color-picker .color-opction a[href]');

    if (!link || !this.contains(link)) return;

    const url = new URL(link.href);

    if (url.origin !== window.location.origin) return;

    const quickAdd = this.activeQuickAddComponent;

    if (!quickAdd) return;

    event.preventDefault();

    quickAdd.handleClick(event, url.href).catch(console.error);
  };


  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}